/**
 * Mail Hub 收件箱列表页
 *
 * 控制面的总入口。优先服务"扫描"和"发现异常"。
 *
 * 必备信息列：组织/邮箱、用途/标签、自动轮询状态、当前运行状态、
 * 最近同步时间、最近结果、待处理摘要、异常标记。
 *
 * → docs/20_inbound_mail_processing/06_mailbox_control_plane_and_observability.md §4
 */

import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    Button,
    Tag,
    Space,
    Tooltip,
    App,
    Badge,
    Select,
    Typography,
    Card,
    Row,
    Col,
    Statistic,
    Modal,
    Form,
    Input,
    InputNumber,
    Switch,
    Popconfirm,
    Alert,
} from "antd";
import {
    ReloadOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    SyncOutlined,
    PlusOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    MailOutlined,
    DeleteOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    listMailboxes,
    listOrganizations,
    createMailbox,
    updateMailbox,
    deleteMailbox,
    triggerMailboxSync,
    getSchedulerStatus,
    getOpsStats,
} from "../../../api/v3/mailHubApi";
import {
    formatMailHubError,
    readMailHubData,
    validateIngestionSummaryPayload,
    validateMailboxListPayload,
    validateOpsStatsPayload,
    validateOrganizationsPayload,
    validateSchedulerStatusPayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { Title, Text } = Typography;

const PROCESSING_MODE_OPTIONS = [
    { value: "observe_only", label: "观察模式", color: "blue" },
    { value: "controlled", label: "受控处理", color: "gold" },
    { value: "full_processing", label: "完整处理", color: "red" },
];

const PROCESSING_CAPABILITIES = [
    { key: "mvt_parse", label: "MVT 解析", supported: true },
    { key: "cpm_parse", label: "CPM 解析", supported: true },
    { key: "ffm_parse", label: "FFM 解析", supported: true },
    { key: "fwb_parse", label: "FWB 解析", supported: true },
    { key: "cgo_attachment_parse", label: "CGO 附件解析", supported: true },
    { key: "ocr_external_call", label: "OCR 外部调用", supported: true, fullProcessingEnabled: true },
    { key: "business_judgment", label: "BJA 研判", supported: true },
    { key: "ucm_forward", label: "UCM 转发入库", supported: true },
    { key: "fuel_forward", label: "燃油报文转发入库", supported: true },
    { key: "hkg_cargo_attachment_forward", label: "香港货运附件转发入库", supported: true },
    { key: "auto_forward", label: "自动转发", supported: false },
    { key: "auto_send", label: "自动发送", supported: true },
];

function modeOption(mode) {
    return PROCESSING_MODE_OPTIONS.find((item) => item.value === mode) || null;
}

/** 轮询状态颜色映射 */
function getPollStatusTag(record) {
    if (!record.is_active) return <Tag color="default">已停用</Tag>;
    if (!record.is_receive_enabled) return <Tag color="default">未启用收信</Tag>;
    if (record.is_poll_enabled) return <Tag color="green">自动轮询中</Tag>;
    return <Tag color="orange">已暂停</Tag>;
}

/** 最近结果标记 */
function getLastResultIndicator(record) {
    if (!record.last_sync_at) {
        return <Tag icon={<ClockCircleOutlined />} color="default">从未同步</Tag>;
    }
    if (record.last_poll_error) {
        return (
            <Tooltip title={record.last_poll_error}>
                <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
            </Tooltip>
        );
    }
    return <Tag icon={<CheckCircleOutlined />} color="success">正常</Tag>;
}

// ---------------------------------------------------------------------------
// 子组件 — 新建收件箱 Modal
// ---------------------------------------------------------------------------

const CreateMailboxModal = ({ open, organizations, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const { message } = App.useApp();
    const watchedMode = Form.useWatch("processing_mode", form);

    const handleModeChange = (nextMode) => {
        if (nextMode === "full_processing") {
            form.setFieldValue("processing_capabilities", PROCESSING_CAPABILITIES.reduce((acc, capability) => {
                acc[capability.key] = capability.fullProcessingEnabled === true;
                return acc;
            }, {}));
        }
    };

    const confirmRiskyCreate = (values) => new Promise((resolve) => {
        const capabilities = values.processing_capabilities || {};
        const enabledCapabilities = PROCESSING_CAPABILITIES
            .filter((capability) => capabilities[capability.key] === true)
            .map((capability) => capability.label);

        if ((values.processing_mode ?? "observe_only") === "observe_only" && enabledCapabilities.length === 0) {
            resolve(true);
            return;
        }

        Modal.confirm({
            title: "确认新建非观察模式邮箱？",
            content: (
                <div>
                    <Text>
                        非观察模式或打开副作用能力后，Mail Hub 可能在后续收信处理中写业务数据、触发 BJA 或外部邮件副作用。请确认这是有意配置。
                    </Text>
                    {enabledCapabilities.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">将打开能力：</Text>
                            <Space wrap style={{ marginLeft: 8 }}>
                                {enabledCapabilities.map((label) => <Tag color="red" key={label}>{label}</Tag>)}
                            </Space>
                        </div>
                    )}
                </div>
            ),
            okText: "确认创建",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (!await confirmRiskyCreate(values)) return;
            setSubmitting(true);

            // 组装凭据（如果提供了用户名和密码）
            const payload = {
                organization_id: values.organization_id,
                email_address: values.email_address,
                display_name: values.display_name || null,
                provider: values.provider || null,
                imap_host: values.imap_host || null,
                imap_port: values.imap_port || null,
                smtp_host: values.smtp_host || null,
                smtp_port: values.smtp_port || null,
                is_receive_enabled: values.is_receive_enabled ?? true,
                is_send_enabled: values.is_send_enabled ?? false,
                is_poll_enabled: values.is_poll_enabled ?? false,
                poll_interval_seconds: values.poll_interval_seconds ?? 300,
                fetch_since_days: values.fetch_since_days ?? 3,
                max_fetch_per_round: values.max_fetch_per_round ?? 50,
                store_attachments_in_oss: values.store_attachments_in_oss ?? false,
                store_inline_images_in_oss: values.store_inline_images_in_oss ?? false,
                processing_mode: values.processing_mode ?? "observe_only",
                processing_capabilities: values.processing_capabilities ?? {},
            };

            // 如果填了凭据用户名，则包含 credential 字段
            if (values.credential_user) {
                payload.credential = {
                    user: values.credential_user,
                    pass: values.credential_pass || undefined,
                };
            }

            await createMailbox(payload);
            message.success(`收件箱 ${values.email_address} 创建成功`);
            form.resetFields();
            onSuccess();
        } catch (err) {
            const errMsg = err?.response?.data?.message || err?.message || "创建失败";
            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title="新建收件箱"
            open={open}
            onCancel={() => { form.resetFields(); onClose(); }}
            onOk={handleSubmit}
            confirmLoading={submitting}
            okText="创建"
            cancelText="取消"
            width={640}
            destroyOnClose
        >
            <Form form={form} layout="vertical" requiredMark="optional">
                {/* 基本信息 */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="organization_id"
                            label="所属组织"
                            rules={[{ required: true, message: "请选择组织" }]}
                        >
                            <Select
                                placeholder="选择组织"
                                options={organizations.map((o) => ({ label: o.name, value: o.id }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="email_address"
                            label="邮箱地址"
                            rules={[
                                { required: true, message: "请输入邮箱地址" },
                                { type: "email", message: "请输入有效邮箱" },
                            ]}
                        >
                            <Input placeholder="user@example.com" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="display_name" label="显示名">
                            <Input placeholder="邮箱显示名称" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="provider" label="提供方">
                            <Input placeholder="如 Gmail, QQ, 企业邮箱" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 连接配置 */}
                <Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>连接配置</Title>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="imap_host" label="IMAP 主机">
                            <Input placeholder="imap.example.com" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="imap_port" label="IMAP 端口">
                            <InputNumber min={1} max={65535} placeholder="993" style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="smtp_host" label="SMTP 主机">
                            <Input placeholder="smtp.example.com" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="smtp_port" label="SMTP 端口">
                            <InputNumber min={1} max={65535} placeholder="465" style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 凭据 */}
                <Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>IMAP 凭据</Title>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="credential_user" label="用户名">
                            <Input placeholder="IMAP 登录用户名" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="credential_pass" label="密码">
                            <Input.Password placeholder="IMAP 登录密码" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 调度配置 */}
                <Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>采集策略</Title>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="poll_interval_seconds" label="轮询间隔 (秒)" initialValue={300}>
                            <InputNumber min={10} max={86400} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="fetch_since_days" label="首次抓取窗口 (天)" initialValue={3}>
                            <InputNumber min={1} max={365} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="max_fetch_per_round" label="单轮上限 (封)" initialValue={50}>
                            <InputNumber min={1} max={1000} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 开关 */}
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="is_receive_enabled" label="启用收信" valuePropName="checked" initialValue={true}>
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="is_poll_enabled" label="启用自动轮询" valuePropName="checked" initialValue={false}>
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="is_send_enabled" label="启用发信" valuePropName="checked" initialValue={false}>
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="store_attachments_in_oss" label="附件落 OSS" valuePropName="checked" initialValue={false}>
                            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="store_inline_images_in_oss" label="内嵌图片落 OSS" valuePropName="checked" initialValue={false}>
                            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>处理模式与副作用</Title>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="默认观察模式"
                    description="观察模式只收信、存储、展示和分类观察，会强制关闭 MVT/CPM/FFM/FWB/CGO 附件解析、BJA、自动发送和自动转发等业务副作用。完整处理当前仅放开 OCR 外部调用，其他业务副作用仍受生产准入白名单阻断。"
                />
                <Form.Item name="processing_mode" label="处理模式" initialValue="observe_only">
                    <Select onChange={handleModeChange} options={PROCESSING_MODE_OPTIONS.map(({ value, label }) => ({ value, label }))} />
                </Form.Item>
                <Row gutter={16}>
                    {PROCESSING_CAPABILITIES.map((capability) => (
                        <Col span={8} key={capability.key}>
                            <Form.Item
                                name={["processing_capabilities", capability.key]}
                                label={capability.label}
                                valuePropName="checked"
                                initialValue={false}
                            >
                                <Switch
                                    checkedChildren="允许"
                                    unCheckedChildren={capability.supported ? "禁止" : "未实现"}
                                    disabled={!capability.supported || watchedMode === "full_processing"}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Form>
        </Modal>
    );
};

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------

const MailboxListPage = () => {
    const navigate = useNavigate();
    const { message, modal } = App.useApp();

    const [loading, setLoading] = useState(true);
    const [mailboxes, setMailboxes] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [opsStats, setOpsStats] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [schedulerError, setSchedulerError] = useState(null);
    const [opsStatsError, setOpsStatsError] = useState(null);
    const [filterOrgId, setFilterOrgId] = useState(undefined);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    // ---- 数据加载 ----
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [mbRes, orgRes, schedulerRes, statsRes] = await Promise.allSettled([
                listMailboxes(filterOrgId),
                listOrganizations(),
                getSchedulerStatus(),
                getOpsStats(),
            ]);

            if (mbRes.status === "rejected") throw mbRes.reason;
            if (orgRes.status === "rejected") throw orgRes.reason;

            const nextMailboxes = validateMailboxListPayload(readMailHubData(mbRes.value, "收件箱列表"));
            const nextOrganizations = validateOrganizationsPayload(readMailHubData(orgRes.value, "组织列表"));

            let nextSchedulerStatus = null;
            let nextSchedulerError = null;
            if (schedulerRes.status === "fulfilled") {
                try {
                    nextSchedulerStatus = validateSchedulerStatusPayload(readMailHubData(schedulerRes.value, "调度器状态"));
                } catch (error) {
                    nextSchedulerError = formatMailHubError(error, "调度器状态不可用");
                }
            } else {
                nextSchedulerError = formatMailHubError(schedulerRes.reason, "调度器状态不可用");
            }

            let nextOpsStats = null;
            let nextOpsStatsError = null;
            if (statsRes.status === "fulfilled") {
                try {
                    nextOpsStats = validateOpsStatsPayload(readMailHubData(statsRes.value, "运维统计"));
                } catch (error) {
                    nextOpsStatsError = formatMailHubError(error, "运维统计不可用");
                }
            } else {
                nextOpsStatsError = formatMailHubError(statsRes.reason, "运维统计不可用");
            }

            setMailboxes(nextMailboxes);
            setOrganizations(nextOrganizations);
            setSchedulerStatus(nextSchedulerStatus);
            setOpsStats(nextOpsStats);
            setLoadError(null);
            setSchedulerError(nextSchedulerError);
            setOpsStatsError(nextOpsStatsError);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载数据失败");
            setMailboxes([]);
            setOrganizations([]);
            setSchedulerStatus(null);
            setOpsStats(null);
            setLoadError(errorMessage);
            setSchedulerError(null);
            setOpsStatsError(null);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [filterOrgId, message]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ---- 动作 ----
    const handleTogglePoll = async (record) => {
        const nextEnabled = !record.is_poll_enabled;
        const actionLabel = nextEnabled ? "启用" : "暂停";
        setActionLoadingId(record.id);
        try {
            await updateMailbox(record.id, { is_poll_enabled: nextEnabled });
            message.success(`已${actionLabel}轮询: ${record.email_address}`);
            await loadData();
        } catch {
            message.error(`${actionLabel}轮询失败`);
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleTriggerSync = async (record) => {
        setActionLoadingId(record.id);
        try {
            const res = await triggerMailboxSync(record.id);
            const summary = validateIngestionSummaryPayload(readMailHubData(res, "手动同步结果"));
            message.success(
                `同步完成: ${record.email_address} — 抓取 ${summary.fetchedCount} 封`,
            );
            await loadData();
        } catch (err) {
            message.error(`触发同步失败: ${err?.response?.data?.message || err.message}`);
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDeactivate = async (record) => {
        setActionLoadingId(record.id);
        try {
            await deleteMailbox(record.id);
            message.success(`已停用: ${record.email_address}`);
            await loadData();
        } catch (err) {
            message.error(`停用失败: ${err?.response?.data?.message || err.message}`);
        } finally {
            setActionLoadingId(null);
        }
    };

    // ---- 组织名 lookup ----
    const orgMap = React.useMemo(() => {
        const m = {};
        for (const org of organizations) {
            m[org.id] = org.name;
        }
        return m;
    }, [organizations]);

    // ---- 调度器运行邮箱 ID 集合 ----
    const pollingMailboxIds = React.useMemo(() => {
        if (!schedulerStatus?.mailboxes) return new Set();
        return new Set(
            schedulerStatus.mailboxes
                .filter((m) => m.isPolling)
                .map((m) => m.mailboxId),
        );
    }, [schedulerStatus]);

    // ---- 表格列定义 ----
    const columns = [
        {
            title: "邮箱",
            key: "mailbox",
            width: 280,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>
                        <MailOutlined style={{ marginRight: 6, color: "#1677ff" }} />
                        {record.email_address}
                    </div>
                    {record.display_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.display_name}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: "组织",
            dataIndex: "organization_id",
            key: "organization",
            width: 140,
            render: (orgId) => <Text>{orgMap[orgId] || orgId?.slice(0, 8)}</Text>,
        },
        {
            title: "轮询状态",
            key: "pollStatus",
            width: 130,
            render: (_, record) => getPollStatusTag(record),
        },
        {
            title: "处理模式",
            key: "processingMode",
            width: 120,
            render: (_, record) => {
                const option = modeOption(record.processing_mode);
                if (!option) {
                    return <Tag color="error">未知处理模式：{record.processing_mode}</Tag>;
                }
                return <Tag color={option.color}>{option.label}</Tag>;
            },
        },
        {
            title: "运行状态",
            key: "runtimeStatus",
            width: 100,
            render: (_, record) => {
                if (schedulerError) {
                    return <Tag color="error">调度状态不可用</Tag>;
                }
                if (pollingMailboxIds.has(record.id)) {
                    return <Badge status="processing" text="同步中" />;
                }
                if (!record.is_poll_enabled) {
                    return <Badge status="default" text="暂停" />;
                }
                return <Badge status="success" text="空闲" />;
            },
        },
        {
            title: "最近同步",
            dataIndex: "last_sync_at",
            key: "lastSync",
            width: 160,
            render: (val) =>
                val ? (
                    <Tooltip title={dayjs(val).format("YYYY-MM-DD HH:mm:ss")}>
                        {dayjs(val).fromNow()}
                    </Tooltip>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: "最近结果",
            key: "lastResult",
            width: 100,
            render: (_, record) => getLastResultIndicator(record),
        },
        {
            title: "异常",
            key: "anomaly",
            width: 80,
            render: (_, record) => {
                const anomalies = [];
                if (record.last_poll_error) anomalies.push("错误");
                if (!record.has_credential) anomalies.push("缺凭据");
                if (
                    record.is_poll_enabled &&
                    record.last_sync_at &&
                    dayjs().diff(dayjs(record.last_sync_at), "hour") > 2
                ) {
                    anomalies.push("长时间未同步");
                }
                if (anomalies.length === 0) return null;
                return (
                    <Tooltip title={anomalies.join("、")}>
                        <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
                    </Tooltip>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 240,
            fixed: "right",
            render: (_, record) => {
                const isLoading = actionLoadingId === record.id;
                if (!record.is_active) {
                    return <Tag color="default">已停用</Tag>;
                }
                return (
                    <Space size="small">
                        <Tooltip title="查看详情">
                            <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => navigate(`/mail-hub/mailboxes/${record.id}`)}
                            />
                        </Tooltip>
                        <Tooltip title={record.is_poll_enabled ? "暂停轮询" : "启用轮询"}>
                            <Button
                                type="link"
                                size="small"
                                loading={isLoading}
                                icon={
                                    record.is_poll_enabled ? (
                                        <PauseCircleOutlined style={{ color: "#faad14" }} />
                                    ) : (
                                        <PlayCircleOutlined style={{ color: "#52c41a" }} />
                                    )
                                }
                                onClick={() => handleTogglePoll(record)}
                            />
                        </Tooltip>
                        <Tooltip title="手动同步">
                            <Button
                                type="link"
                                size="small"
                                loading={isLoading}
                                icon={<SyncOutlined />}
                                onClick={() => handleTriggerSync(record)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="确认停用"
                            description={`停用 ${record.email_address}？停用后将退出自动轮询，但配置和历史数据保留。`}
                            onConfirm={() => handleDeactivate(record)}
                            okText="停用"
                            okButtonProps={{ danger: true }}
                            cancelText="取消"
                        >
                            <Tooltip title="停用邮箱">
                                <Button
                                    type="link"
                                    size="small"
                                    loading={isLoading}
                                    icon={<StopOutlined style={{ color: "#ff4d4f" }} />}
                                    danger
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    // ---- 渲染 ----
    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                    <MailOutlined style={{ marginRight: 8 }} />
                    收件箱控制面
                </Title>
                <Space>
                    <Select
                        allowClear
                        placeholder="按组织筛选"
                        style={{ width: 200 }}
                        value={filterOrgId}
                        onChange={setFilterOrgId}
                        options={organizations.map((o) => ({ label: o.name, value: o.id }))}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setCreateModalOpen(true)}
                    >
                        新建收件箱
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                        刷新
                    </Button>
                </Space>
            </div>

            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="收件箱控制面数据不可用"
                    description={loadError}
                />
            ) : null}

            {!loadError ? (
                <>
                    {/* 概览统计卡片 */}
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                            <Card size="small" loading={loading && mailboxes.length === 0}>
                                <Statistic
                                    title="总邮箱数"
                                    value={mailboxes.length}
                                    prefix={<MailOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small" loading={loading && mailboxes.length === 0}>
                                <Statistic
                                    title="轮询中"
                                    value={mailboxes.filter((m) => m.is_poll_enabled && m.is_active).length}
                                    valueStyle={{ color: "#52c41a" }}
                                    prefix={<PlayCircleOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                {schedulerError ? (
                                    <Alert type="error" showIcon message="调度器状态不可用" description={schedulerError} />
                                ) : (
                                    <Statistic
                                        title="调度器活跃"
                                        value={schedulerStatus ? schedulerStatus.mailboxes.length : "—"}
                                        valueStyle={{ color: "#1677ff" }}
                                        prefix={<SyncOutlined />}
                                    />
                                )}
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                {opsStatsError ? (
                                    <Alert type="error" showIcon message="待处理事件统计不可用" description={opsStatsError} />
                                ) : (
                                    <Statistic
                                        title="待处理事件"
                                        value={opsStats ? opsStats.pendingInboundEvents : "—"}
                                        valueStyle={
                                            opsStats && opsStats.failedEvents > 0
                                                ? { color: "#ff4d4f" }
                                                : undefined
                                        }
                                        prefix={<ExclamationCircleOutlined />}
                                        suffix={
                                            opsStats && opsStats.failedEvents > 0 ? (
                                                <Text type="danger" style={{ fontSize: 12 }}>
                                                    ({opsStats.failedEvents} 失败)
                                                </Text>
                                            ) : null
                                        }
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>

                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={mailboxes}
                        loading={loading}
                        pagination={false}
                        scroll={{ x: 1300 }}
                        size="middle"
                    />
                </>
            ) : null}

            <CreateMailboxModal
                open={createModalOpen}
                organizations={organizations}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={() => { setCreateModalOpen(false); loadData(); }}
            />
        </div>
    );
};

export default MailboxListPage;
